<template>
    <div class="container-fluid">
        <PageHeader :data="{
            name: getMunicipalityName() + ' - ' + getMunicipalityType(),
            breadcrumbs: [
                { name: 'Home', to: '/' },
                { name: 'Platform Fingerprints', to: '/competitors' },
            ]
        }" />

        <div>
            <!-- Date Range Filter at Top -->
            <div class="card mb-4">
                <div class="card-body">
                    <h5 class="mb-3">Select Date Range</h5>
                    <DateRangeFilter
                        :start-date="migrationStartDate"
                        :end-date="migrationEndDate"
                        @update:startDate="migrationStartDate = $event"
                        @update:endDate="migrationEndDate = $event"
                        @apply="applyDateFilter"
                        @reset="resetDateRange"
                    />
                </div>
            </div>

            <!-- Current Analysis Info -->
            <div class="mb-4">
                <h3 class="h4 mb-2">Competitor Analytics</h3>
                <p class="text-muted">
                    Range: {{ migrationStartDate }} → {{ migrationEndDate }}
                </p>
            </div>

            <div v-if="platformDetails.analytics" class="row mb-4">
                <div class="col-md-3">
                    <KpiCard :value="getLatestMarketShare()" label="Latest Market Share" format="percent" color="primary" />
                </div>
                <div class="col-md-3">
                    <KpiCard :value="getLatestCustomerCount()" label="Latest Customer Count" format="number" color="success" />
                </div>
                <div class="col-md-3">
                    <KpiCard :value="getLatestNetChange()" label="Latest Net Change" format="signed" />
                </div>
                <div class="col-md-3">
                    <KpiCard :value="getTotalMarketSize()" label="Total Market Size" format="number" color="info" />
                </div>
            </div>

            <!-- Population Statistics -->
            <div v-if="populationStats" class="row mb-4">
                <div class="col-12 mb-2">
                    <h6 class="text-muted mb-0">Client Population Statistics <small class="text-muted">(Click to filter)</small></h6>
                </div>
                <div class="col-md-3">
                    <KpiCard
                        :value="populationStats.min"
                        label="Min Population"
                        subtitle="Smallest Client"
                        format="number"
                        color="secondary"
                        :clickable="true"
                        @click="navigateToPopulation('min', populationStats.min)"
                    />
                </div>
                <div class="col-md-3">
                    <KpiCard
                        :value="populationStats.max"
                        label="Max Population"
                        subtitle="Largest Client"
                        format="number"
                        color="secondary"
                        :clickable="true"
                        @click="navigateToPopulation('max', populationStats.max)"
                    />
                </div>
                <div class="col-md-3">
                    <KpiCard
                        :value="populationStats.median"
                        label="Median Population"
                        subtitle="Middle Value"
                        format="number"
                        color="secondary"
                        :clickable="true"
                        @click="navigateToPopulation('median', populationStats.median)"
                    />
                </div>
                <div class="col-md-3">
                    <KpiCard
                        :value="populationStats.average"
                        label="Average Population"
                        subtitle="Mean Value"
                        format="number"
                        color="secondary"
                        :clickable="true"
                        @click="navigateToPopulation('average', populationStats.average)"
                    />
                </div>
            </div>

            <!-- Platform Migration Charts -->
            <div class="row mb-4">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <div class="d-flex align-items-center gap-2">
                                <h6 class="mb-0">Platform Migration Analytics</h6>
                                <span class="badge bg-primary">
                                    <i class="fas fa-calendar-alt"></i> {{ formatDateRange() }}
                                </span>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="card chart-card border">
                                        <div class="card-header bg-transparent">
                                            <h2 class="h6 mb-0">Joining Platforms ({{ getJoiningTotal().toLocaleString() }})</h2>
                                        </div>
                                        <div class="card-body">
                                            <div class="chart-wrap">
                                                <div v-if="chartLoading.joining" class="d-flex justify-content-center align-items-center" style="height: 300px;">
                                                    <div class="spinner-border text-primary" role="status">
                                                        <span class="visually-hidden">Loading joining chart...</span>
                                                    </div>
                                                </div>
                                                <canvas v-else ref="joiningChart"></canvas>
                                            </div>
                                            <div v-if="!chartLoading.joining && joiningLegendItems.length" class="d-flex flex-wrap justify-content-center mt-3">
                                                <div 
                                                    v-for="(item, index) in joiningLegendItems" 
                                                    :key="index"
                                                    class="d-flex align-items-center me-3 mb-2 cursor-pointer"
                                                    :class="{ 'opacity-50': hiddenJoiningDatasets.includes(index) }"
                                                    @click="toggleJoiningDataset(index)"
                                                    :title="hiddenJoiningDatasets.includes(index) ? 'Click to show' : 'Click to hide'"
                                                >
                                                    <div 
                                                        class="me-2 rounded"
                                                        :style="`width: 16px; height: 16px; background-color: ${item.color}; flex-shrink: 0;`"
                                                    ></div>
                                                    <div 
                                                        v-if="item.logo"
                                                        class="me-2 rounded d-flex align-items-center justify-content-center"
                                                        style="width: 40px; height: 40px; background-color: white; padding: 2px; flex-shrink: 0;"
                                                    >
                                                        <img 
                                                            :src="item.logo.startsWith('http') ? item.logo : storageUrl + item.logo" 
                                                            :alt="item.name + ' logo'"
                                                            style="width: 100%; height: 100%; object-fit: contain;"
                                                            @error="$event.target.style.display='none'"
                                                        />
                                                    </div>
                                                    <small v-else class="text-muted">{{ item.name }}</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="card chart-card border">
                                        <div class="card-header bg-transparent">
                                            <h2 class="h6 mb-0">Leaving Platforms ({{ getLeavingTotal().toLocaleString() }})</h2>
                                        </div>
                                        <div class="card-body">
                                            <div class="chart-wrap">
                                                <div v-if="chartLoading.leaving" class="d-flex justify-content-center align-items-center" style="height: 300px;">
                                                    <div class="spinner-border text-primary" role="status">
                                                        <span class="visually-hidden">Loading leaving chart...</span>
                                                    </div>
                                                </div>
                                                <canvas v-else ref="leavingChart"></canvas>
                                            </div>
                                            <div v-if="!chartLoading.leaving && leavingLegendItems.length" class="d-flex flex-wrap justify-content-center mt-3">
                                                <div 
                                                    v-for="(item, index) in leavingLegendItems" 
                                                    :key="index"
                                                    class="d-flex align-items-center me-3 mb-2 cursor-pointer"
                                                    :class="{ 'opacity-50': hiddenLeavingDatasets.includes(index) }"
                                                    @click="toggleLeavingDataset(index)"
                                                    :title="hiddenLeavingDatasets.includes(index) ? 'Click to show' : 'Click to hide'"
                                                >
                                                    <div 
                                                        class="me-2 rounded"
                                                        :style="`width: 16px; height: 16px; background-color: ${item.color}; flex-shrink: 0;`"
                                                    ></div>
                                                    <div 
                                                        v-if="item.logo"
                                                        class="me-2 rounded d-flex align-items-center justify-content-center"
                                                        style="width: 40px; height: 40px; background-color: white; padding: 2px; flex-shrink: 0;"
                                                    >
                                                        <img 
                                                            :src="item.logo.startsWith('http') ? item.logo : storageUrl + item.logo" 
                                                            :alt="item.name + ' logo'"
                                                            style="width: 100%; height: 100%; object-fit: contain;"
                                                            @error="$event.target.style.display='none'"
                                                        />
                                                    </div>
                                                    <small v-else class="text-muted">{{ item.name }}</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Weekly Checks Chart -->
            <div class="row mb-4">
                <div class="col-12">
                    <div class="card chart-card">
                        <div class="card-header">
                            <div class="d-flex align-items-center gap-2">
                                <h2 class="h6 mb-0">Weekly Checks</h2>
                                <span class="badge bg-primary">
                                    <i class="fas fa-calendar-alt"></i> {{ formatDateRange() }}
                                </span>
                            </div>
                            <small class="text-muted">Weekly monitoring and check activities</small>
                        </div>
                        <div class="card-body">
                            <div class="chart-wrap">
                                <div v-if="chartLoading.weeklyChecks" class="d-flex justify-content-center align-items-center" style="height: 300px;">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">Loading weekly checks chart...</span>
                                    </div>
                                </div>
                                <canvas v-else ref="weeklyChecksChart"></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- FROTO Migration View -->
            <div class="row mb-4">
                <div class="col-12">
                    <FrotoMigrationTable
                        :frotoData="frotoData"
                        :loading="frotoLoading"
                        :date-range="formatDateRange()"
                    />
                </div>
            </div>

            <!-- US State Heat Map -->
            <div class="row mb-4">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h2 class="h6 mb-0">Geographic Distribution</h2>
                            <small class="text-muted">Client distribution across US states</small>
                        </div>
                        <div class="card-body">
                            <USStateHeatMap
                                :stateData="stateDistributionData"
                                :loading="chartLoading.stateDistribution"
                                :colorScheme="'blue'"
                                :showLabels="false"
                                :platformId="platformId"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- <div class="row mb-4">
                <div class="col-md-6">
                    <div class="card chart-card">
                        <div class="card-header">
                            <h2 class="h6 mb-0">Market Share Trend</h2>
                            <small class="text-muted">Market Share = (This Platform's Customers) ÷ (Total Customers in Same Platform Category) × 100</small>
                        </div>
                        <div class="card-body">
                            <div class="chart-wrap">
                                <canvas ref="marketShareChart"></canvas>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="card chart-card">
                        <div class="card-header">
                            <h2 class="h6 mb-0">Net Change</h2>
                        </div>
                        <div class="card-body">
                            <div class="chart-wrap">
                                <canvas ref="netChangeChart"></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </div> -->


            <div v-if="!platformDetails.analytics && !chartLoading.joining && !chartLoading.leaving && !chartLoading.weeklyChecks" class="text-center text-muted py-4">
                <h5>No platform data available</h5>
                <p>Unable to load platform fingerprint details for ID: {{ platformId }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader.vue'
import USStateHeatMap from '@/components/charts/USStateHeatMap.vue'
import FrotoMigrationTable from '@/components/competitor/FrotoMigrationTable.vue'
import KpiCard from '@/components/common/KpiCard.vue'
import DateRangeFilter from '@/components/common/DateRangeFilter.vue'
import restApi from '../../api'
import { parse, format, isAfter, startOfWeek } from 'date-fns'
import Chart from 'chart.js/auto'
import { mapState } from 'vuex'

export default {
    name: 'Competitor',
    components: { PageHeader, USStateHeatMap, FrotoMigrationTable, KpiCard, DateRangeFilter },
    data() {
        return {
            platformDetails: { analytics: null },
            platformFingerprint: { name: null, type: null },
            weeklyChecksData: [],
            migrationStartDate: '',
            migrationEndDate: '',
            charts: {
                marketShare: null,
                netChange: null,
                joining: null,
                leaving: null,
                weeklyChecks: null
            },
            chartLoading: {
                joining: false,
                leaving: false,
                weeklyChecks: false,
                stateDistribution: false
            },
            previousQuery: null,
            joiningLegendItems: [],
            leavingLegendItems: [],
            hiddenJoiningDatasets: [],
            hiddenLeavingDatasets: [],
            joiningChartData: null,
            leavingChartData: null,
            stateDistributionData: [],
            populationStats: null,
            frotoData: [],
            frotoLoading: false
        }
    },
    computed: {
        platformId() { return this.$route.params.id },
        ...mapState(['storageUrl'])
    },
    watch: {
        '$route.query': {
            handler() { this.syncFromQuery() },
            immediate: true
        }
    },
    async mounted() {
        if (this.platformId) {
            try {
                await this.fetchPlatformFingerprint(this.platformId)
                // Geographic distribution is not tied to the date range,
                // so we load it once per platform on mount.
                await this.fetchStateDistribution(this.platformId)
            } catch (error) {
                console.error('Error during component initialization:', error)
            }
        }
    },
    methods: {
        parsePeriod(period, timeGroup) {
            if (!period) return new Date()

            switch (timeGroup) {
                case 'daily':
                case 'weekly':
                    return parse(period, 'yyyy-MM-dd', new Date())
                case 'monthly':
                    if (period.match(/^\d{4}-\d{1,2}$/)) {
                        return parse(period + '-01', 'yyyy-MM-dd', new Date())
                    }
                    return new Date(period)
                case 'yearly':
                    if (period.match(/^\d{4}$/)) {
                        return parse(period + '-01-01', 'yyyy-MM-dd', new Date())
                    }
                    return new Date(period)
                default:
                    return new Date(period)
            }
        },

        formatPeriodLabel(period, timeGroup) {
            if (!period) return ''

            const date = this.parsePeriod(period, timeGroup)
            if (isNaN(date)) return period

            switch (timeGroup) {
                case 'daily':
                    return format(date, 'd MMM yyyy')
                case 'weekly':
                    return format(date, 'MMM d, yyyy')
                case 'monthly':
                    return format(date, 'MMM yyyy')
                case 'yearly':
                    return format(date, 'yyyy')
                default:
                    return period
            }
        },

        sortPeriods(periods, timeGroup) {
            return periods.sort((a, b) => {
                const dateA = this.parsePeriod(a, timeGroup)
                const dateB = this.parsePeriod(b, timeGroup)
                return isAfter(dateA, dateB) ? 1 : -1
            })
        },

        calculateNetChange(joining, leaving, selectedTimeGroup) {
            const periodMap = new Map()
            
            joining.forEach(item => {
                const period = item.period || this.extractPeriodFromData(item, selectedTimeGroup)
                if (period) {
                    const count = item.municipality_count || 0
                    periodMap.set(period, (periodMap.get(period) || 0) + count)
                }
            })
            
            leaving.forEach(item => {
                const period = item.period || this.extractPeriodFromData(item, selectedTimeGroup)
                if (period) {
                    const count = item.municipality_count || 0
                    periodMap.set(period, (periodMap.get(period) || 0) - count)
                }
            })
            
            return Array.from(periodMap.entries()).map(([period, net_change]) => ({
                period,
                net_change
            }))
        },

        calculateMarketShare(joining, leaving, selectedTimeGroup) {
            const periodMap = new Map()
            
            joining.forEach(item => {
                const period = item.period || this.extractPeriodFromData(item, selectedTimeGroup)
                if (period) {
                    const count = item.municipality_count || 0
                    periodMap.set(period, (periodMap.get(period) || 0) + count)
                }
            })
            
            leaving.forEach(item => {
                const period = item.period || this.extractPeriodFromData(item, selectedTimeGroup)
                if (period) {
                    const count = item.municipality_count || 0
                    periodMap.set(period, (periodMap.get(period) || 0) - count)
                }
            })
            
            let runningTotal = 0
            const sortedPeriods = this.sortPeriods(Array.from(periodMap.keys()), selectedTimeGroup)
            
            return sortedPeriods.map(period => {
                runningTotal += periodMap.get(period) || 0
                return {
                    period,
                    customer_count: Math.max(0, runningTotal),
                    market_share_percentage: runningTotal > 0 ? Math.min(100, (runningTotal / 10000) * 100) : 0
                }
            })
        },

        extractPeriodFromData(item, selectedTimeGroup) {
            if (item.period) return item.period
            if (item.date) {
                const date = new Date(item.date)
                switch (selectedTimeGroup) {
                    case 'daily':
                        return format(date, 'yyyy-MM-dd')
                    case 'weekly': {
                        const monday = startOfWeek(date, { weekStartsOn: 1 })
                        return format(monday, 'yyyy-MM-dd')
                    }
                    case 'monthly':
                        return format(date, 'yyyy-MM')
                    case 'yearly':
                        return format(date, 'yyyy')
                    default:
                        return item.date
                }
            }
            return null
        },

        syncFromQuery() {
            const query = this.$route.query
            const endDate = new Date()
            const startDate = new Date()
            startDate.setMonth(startDate.getMonth() - 2)

            const newStartDate = query.startDate || startDate.toISOString().split('T')[0]
            const newEndDate = query.endDate || endDate.toISOString().split('T')[0]

            this.migrationStartDate = newStartDate
            this.migrationEndDate = newEndDate

            if (this.platformId) {
                this.fetchMunicipalityDetails(this.platformId)
                this.fetchPlatformChecks(this.platformId)
                this.fetchPopulationStats(this.platformId)
                this.fetchFrotoData(this.platformId)
            }

            this.previousQuery = { startDate: newStartDate, endDate: newEndDate }
        },

        updateQuery() {
            const query = {
                ...this.$route.query,
                startDate: this.migrationStartDate,
                endDate: this.migrationEndDate
            }

            if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
                this.$router.replace({ query })
            }
        },

        applyDateFilter() {
            // Dates are already updated via @update:startDate and @update:endDate
            // Just trigger the query update to fetch new data
            this.updateQuery()
        },

        resetDateRange() {
            const endDate = new Date()
            const startDate = new Date()
            startDate.setMonth(startDate.getMonth() - 2)
            
            this.migrationEndDate = endDate.toISOString().split('T')[0]
            this.migrationStartDate = startDate.toISOString().split('T')[0]
            this.updateQuery()
        },


        async fetchMunicipalityDetails(platformId) {
            this.chartLoading.joining = true
            this.chartLoading.leaving = true
            try {
                const response = await restApi.get(`/platform-fingerprints/${platformId}/analytics`, {
                    params: {
                        start_date: this.migrationStartDate,
                        end_date: this.migrationEndDate
                    }
                })
                
                if (response.data) {
                    const analytics = (response.data.data && response.data.data.analytics) || response.data.analytics
                    
                    const transformedAnalytics = {
                        ...analytics,
                        joining_platform: analytics?.joining_platform || [],
                        leaving_platform: analytics?.leaving_platform || [],
                        net_change: (analytics?.net_change || []).map(item => ({
                            ...item,
                            net_change: parseInt(item.net_change || 0)
                        })),
                        market_share: (analytics?.market_share || []).map(item => ({
                            ...item,
                            market_share_percentage: parseFloat(item.market_share_percentage || 0)
                        }))
                    }
                    
                    this.platformDetails = {
                        ...response.data,
                        analytics: transformedAnalytics
                    }
                    
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.initializeCharts()
                        }, 100)
                    })
                }
            } catch (error) {
                console.error('Error fetching municipality details:', error)
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error loading platform details',
                    text: (error.response && error.response.data && error.response.data.message) || error.message
                })
            } finally {
                this.chartLoading.joining = false
                this.chartLoading.leaving = false
            }
        },

        async fetchPlatformFingerprint(platformId) {
            try {
                const response = await restApi.get(`/platform-fingerprints/${platformId}`)
                
                if (response.data && (response.data.name || (response.data.data && response.data.data.name))) {
                    const data = response.data.data || response.data
                    this.platformFingerprint = {
                        name: data.name || 'Platform ID ' + platformId,
                        type: data.type || 'Platform'
                    }
                } else {
                    this.platformFingerprint = {
                        name: 'Platform ID ' + platformId,
                        type: 'Migration Analytics'
                    }
                }
            } catch (error) {
                console.error('Error fetching platform fingerprint details:', error)
                this.platformFingerprint = {
                    name: 'Platform ID ' + platformId,
                    type: 'Migration Analytics'
                }
            }
        },

        async fetchPlatformChecks(platformId) {
            this.chartLoading.weeklyChecks = true
            try {
                const { data } = await restApi.get('/platform-fingerprints/checks', {
                    params: {
                        platform_fingerprint_ids: platformId,
                        start_date: this.migrationStartDate,
                        end_date: this.migrationEndDate
                    }
                })
                this.weeklyChecksData = data || []

                this.$nextTick(() => {
                    setTimeout(() => {
                        this.createWeeklyChecksChart()
                    }, 100)
                })
            } catch (error) {
                console.error('Error fetching platform checks:', error)
                this.weeklyChecksData = []
            } finally {
                this.chartLoading.weeklyChecks = false
            }
        },

        async fetchStateDistribution(platformId) {
            this.chartLoading.stateDistribution = true
            try {
                const { data } = await restApi.get(`/platform-fingerprints/${platformId}/state-distribution`)
                this.stateDistributionData = data.state_distribution || []
            } catch (error) {
                console.error('Error fetching state distribution:', error)
                this.stateDistributionData = []
            } finally {
                this.chartLoading.stateDistribution = false
            }
        },

        async fetchPopulationStats(platformId) {
            try {
                const { data } = await restApi.get(`/platform-fingerprints/${platformId}/population-stats`)
                this.populationStats = data || null
            } catch (error) {
                console.error('Error fetching population stats:', error)
                this.populationStats = null
            }
        },

        navigateToPopulation(type, value) {
            const query = {
                provider: this.platformId
            }

            switch (type) {
                case 'min':
                    query.frompopulation = value
                    query.topopulation = value
                    break
                case 'max':
                    query.frompopulation = value
                    query.topopulation = value
                    break
                case 'median':
                    query.frompopulation = Math.floor(value * 0.8)
                    query.topopulation = Math.ceil(value * 1.2)
                    break
                case 'average':
                    query.frompopulation = Math.floor(value * 0.8)
                    query.topopulation = Math.ceil(value * 1.2)
                    break
            }

            const url = this.$router.resolve({
                path: '/municipalities',
                query
            }).href

            window.open(url, '_blank')
        },

        formatNumber(num) {
            if (!num && num !== 0) return '-'
            return num.toLocaleString()
        },

        formatDateRange() {
            if (!this.migrationStartDate || !this.migrationEndDate) return ''
            const start = format(new Date(this.migrationStartDate), 'MMM d, yyyy')
            const end = format(new Date(this.migrationEndDate), 'MMM d, yyyy')
            return `${start} - ${end}`
        },

        async fetchFrotoData(platformId) {
            this.frotoLoading = true
            try {
                const { data } = await restApi.get(`/platform-fingerprints/${platformId}/froto`, {
                    params: {
                        start_date: this.migrationStartDate,
                        end_date: this.migrationEndDate
                    }
                })
                this.frotoData = data || []
            } catch (error) {
                console.error('Error fetching FROTO data:', error)
                this.frotoData = []
            } finally {
                this.frotoLoading = false
            }
        },

        getMunicipalityName() {
            return this.platformFingerprint.name || 'Platform ID ' + this.platformId
        },

        getMunicipalityType() {
            return this.platformFingerprint.type || 'Analytics'
        },

        getLatestMarketShare() {
            if (!this.platformDetails.analytics) return 0
            return this.platformDetails.analytics.current_market_share ? 
                parseFloat(this.platformDetails.analytics.current_market_share).toFixed(1) : 0
        },

        getLatestCustomerCount() {
            if (!this.platformDetails.analytics) return 0
            return this.platformDetails.analytics.current_customer_count || 0
        },

        getLatestNetChange() {
            if (!this.platformDetails.analytics) return 0
            return this.platformDetails.analytics.current_net_change || 0
        },

        getTotalMarketSize() {
            if (!this.platformDetails.analytics) return 0
            return this.platformDetails.analytics.total_market_size || 0
        },


        getJoiningTotal() {
            if (!this.platformDetails.analytics || !this.platformDetails.analytics.joining_platform) return 0
            return this.platformDetails.analytics.joining_platform
                .reduce((sum, item) => sum + (item.municipality_count || 0), 0)
        },

        getLeavingTotal() {
            if (!this.platformDetails.analytics || !this.platformDetails.analytics.leaving_platform) return 0
            return this.platformDetails.analytics.leaving_platform
                .reduce((sum, item) => sum + (item.municipality_count || 0), 0)
        },


        async initializeCharts() {
            if (!this.platformDetails.analytics) {
                return
            }

            ['marketShare', 'netChange', 'joining', 'leaving'].forEach(key => {
                if (this.charts[key]) {
                    this.charts[key].destroy()
                    this.charts[key] = null
                }
            })

            // Clear legend items and hidden datasets
            this.joiningLegendItems = []
            this.leavingLegendItems = []
            this.hiddenJoiningDatasets = []
            this.hiddenLeavingDatasets = []

            this.createMarketShareChart()
            this.createNetChangeChart()
            this.createJoiningChart()
            this.createLeavingChart()
        },

        createMarketShareChart() {
            const canvas = this.$refs.marketShareChart
            if (!canvas) return

            const analytics = this.platformDetails.analytics
            if (!analytics.market_share || analytics.market_share.length === 0) {
                return
            }

            const data = this.prepareMarketShareData(analytics.market_share)
            this.charts.marketShare = new Chart(canvas, {
                type: 'line',
                data,
                options: this.getMarketShareOptions()
            })
        },

        createNetChangeChart() {
            const canvas = this.$refs.netChangeChart
            if (!canvas) return

            const analytics = this.platformDetails.analytics
            if (!analytics.net_change || analytics.net_change.length === 0) {
                return
            }

            const data = this.prepareNetChangeData(analytics.net_change)
            this.charts.netChange = new Chart(canvas, {
                type: 'bar',
                data,
                options: this.getNetChangeOptions()
            })
        },

        createJoiningChart() {
            const canvas = this.$refs.joiningChart
            if (!canvas) {
                return
            }

            const analytics = this.platformDetails.analytics
            if (!analytics.joining_platform || analytics.joining_platform.length === 0) {
                return
            }

            const data = this.prepareStackedBarData(analytics.joining_platform, 'from_platform')
            this.joiningChartData = data
            this.charts.joining = new Chart(canvas, {
                type: 'bar',
                data,
                options: this.getStackedBarOptions()
            })
            this.joiningLegendItems = data.datasets.map(d => ({
                name: d.label,
                color: d.backgroundColor,
                logo: d.logoUrl || null
            }))
            this.hiddenJoiningDatasets = []
        },

        createLeavingChart() {
            const canvas = this.$refs.leavingChart
            if (!canvas) {
                return
            }

            const analytics = this.platformDetails.analytics
            if (!analytics.leaving_platform || analytics.leaving_platform.length === 0) {
                return
            }

            const data = this.prepareStackedBarData(analytics.leaving_platform, 'to_platform')
            this.leavingChartData = data
            this.charts.leaving = new Chart(canvas, {
                type: 'bar',
                data,
                options: this.getStackedBarOptions()
            })
            this.leavingLegendItems = data.datasets.map(d => ({
                name: d.label,
                color: d.backgroundColor,
                logo: d.logoUrl || null
            }))
            this.hiddenLeavingDatasets = []
        },

        createWeeklyChecksChart() {
            // Destroy existing chart if it exists
            if (this.charts.weeklyChecks) {
                this.charts.weeklyChecks.destroy()
                this.charts.weeklyChecks = null
            }

            const canvas = this.$refs.weeklyChecksChart
            if (!canvas) {
                return
            }

            const data = this.prepareWeeklyChecksData(this.weeklyChecksData)
            
            this.charts.weeklyChecks = new Chart(canvas, {
                type: 'line',
                data,
                options: this.getWeeklyChecksOptions()
            })
        },

        toggleJoiningDataset(index) {
            const hiddenIndex = this.hiddenJoiningDatasets.indexOf(index)
            if (hiddenIndex > -1) {
                this.hiddenJoiningDatasets.splice(hiddenIndex, 1)
            } else {
                this.hiddenJoiningDatasets.push(index)
            }
            this.updateJoiningChart()
        },

        toggleLeavingDataset(index) {
            const hiddenIndex = this.hiddenLeavingDatasets.indexOf(index)
            if (hiddenIndex > -1) {
                this.hiddenLeavingDatasets.splice(hiddenIndex, 1)
            } else {
                this.hiddenLeavingDatasets.push(index)
            }
            this.updateLeavingChart()
        },

        updateJoiningChart() {
            if (!this.charts.joining || !this.joiningChartData) return
            // Restore all datasets first by deep cloning
            this.charts.joining.data.datasets = this.joiningChartData.datasets.map(d => ({
                ...d,
                data: [...d.data],
                originalData: [...d.originalData]
            }))
            // Then hide the ones that should be hidden
            this.charts.joining.data.datasets.forEach((dataset, idx) => {
                dataset.hidden = this.hiddenJoiningDatasets.includes(idx)
            })
            this.charts.joining.update()
        },

        updateLeavingChart() {
            if (!this.charts.leaving || !this.leavingChartData) return
            // Restore all datasets first by deep cloning
            this.charts.leaving.data.datasets = this.leavingChartData.datasets.map(d => ({
                ...d,
                data: [...d.data],
                originalData: [...d.originalData]
            }))
            // Then hide the ones that should be hidden
            this.charts.leaving.data.datasets.forEach((dataset, idx) => {
                dataset.hidden = this.hiddenLeavingDatasets.includes(idx)
            })
            this.charts.leaving.update()
        },

        normalizeLogoUrl(logo) {
            if (!logo) return null
            if (logo.startsWith('http')) return logo
            const path = logo.startsWith('/') ? logo : `/${logo}`
            const base = this.storageUrl || ''
            return `${base}${path}`
        },


        prepareWeeklyChecksData(weeklyChecksData) {
            // Handle empty data case
            if (!Array.isArray(weeklyChecksData) || weeklyChecksData.length === 0) {
                return {
                    labels: [],
                    datasets: [{
                        label: 'Weekly Checks',
                        data: [],
                        borderColor: '#8b5cf6',
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#8b5cf6',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6
                    }]
                }
            }

            // Simple processing matching Competitors.vue pattern
            const sortedData = weeklyChecksData.sort((a, b) => new Date(a.date) - new Date(b.date))
            
            const labels = sortedData.map(item => {
                const date = new Date(item.date)
                return format(date, 'MMM d')
            })
            
            const checksData = sortedData.map(item => item.count || 0)

            return {
                labels,
                datasets: [{
                    label: 'Weekly Checks',
                    data: checksData,
                    borderColor: '#8b5cf6',
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#8b5cf6',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }]
            }
        },

        prepareMarketShareData(marketShareData) {
            const sortedPeriods = this.sortPeriods(marketShareData.map(item => item.period), 'weekly')
            const labels = sortedPeriods.map(period => this.formatPeriodLabel(period, 'weekly'))
            
            const customerCounts = sortedPeriods.map(period => {
                const item = marketShareData.find(d => d.period === period)
                return item ? item.customer_count || 0 : 0
            })
            const marketShares = sortedPeriods.map(period => {
                const item = marketShareData.find(d => d.period === period)
                return item ? item.market_share_percentage || 0 : 0
            })

            return {
                labels,
                datasets: [{
                    label: 'Customer Count',
                    data: customerCounts,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    yAxisID: 'y'
                }, {
                    label: 'Market Share %',
                    data: marketShares,
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    yAxisID: 'y1'
                }]
            }
        },

        prepareNetChangeData(netChangeData) {
            const sortedPeriods = this.sortPeriods(netChangeData.map(item => item.period), 'weekly')
            const labels = sortedPeriods.map(period => this.formatPeriodLabel(period, 'weekly'))
            const values = sortedPeriods.map(period => {
                const item = netChangeData.find(d => d.period === period)
                return item ? item.net_change || 0 : 0
            })

            const total = values.reduce((sum, val) => sum + val, 0)
            labels.push('Total')
            values.push(total)

            return {
                labels,
                datasets: [{
                    label: 'Net Change',
                    data: values,
                    backgroundColor: values.map(val => val >= 0 ? '#10b981' : '#ef4444'),
                    borderColor: values.map(val => val >= 0 ? '#059669' : '#dc2626'),
                    borderWidth: 1
                }]
            }
        },

        prepareStackedBarData(platformData, platformField) {
            const dataWithPeriods = platformData.map(item => ({
                ...item,
                period: item.period || this.extractPeriodFromData(item, 'weekly')
            }))

            const platforms = [...new Set(dataWithPeriods.map(item => item[platformField]))].filter(Boolean)
            const periods = [...new Set(dataWithPeriods.map(item => item.period))].filter(Boolean)
            const sortedPeriods = this.sortPeriods(periods, 'weekly')

            const labels = sortedPeriods.map(period => {
                const periodTotal = dataWithPeriods
                    .filter(item => item.period === period)
                    .reduce((sum, item) => sum + (item.municipality_count || 0), 0)

                return `${this.formatPeriodLabel(period, 'weekly')} (${periodTotal.toLocaleString()})`
            })

            const platformTotals = platforms.map(platform => {
                const total = dataWithPeriods.filter(item => item[platformField] === platform)
                    .reduce((sum, item) => sum + (item.municipality_count || 0), 0)
                const firstItem = dataWithPeriods.find(item => item[platformField] === platform)
                return {
                    platform,
                    total,
                    color: firstItem?.color,
                    logo: firstItem?.logo
                }
            }).sort((a, b) => b.total - a.total)

            const datasets = platformTotals.map((platformInfo) => {
                const platformValues = sortedPeriods.map(period => {
                    const item = dataWithPeriods.find(d => d.period === period && d[platformField] === platformInfo.platform)
                    const originalValue = item ? item.municipality_count || 0 : 0
                    const transformedValue = originalValue > 0 ? Math.sqrt(originalValue) * 10 : 0
                    return transformedValue
                })

                const originalValues = sortedPeriods.map(period => {
                    const item = dataWithPeriods.find(d => d.period === period && d[platformField] === platformInfo.platform)
                    return item ? item.municipality_count || 0 : 0
                })

                return {
                    label: platformInfo.platform,
                    data: platformValues,
                    originalData: originalValues,
                    backgroundColor: platformInfo.color || this.getPlatformColor(platformInfo.platform),
                    borderWidth: 1,
                    logo: platformInfo.logo,
                    logoUrl: this.normalizeLogoUrl(platformInfo.logo)
                }
            })

            return { labels, datasets }
        },

        // Get consistent color for a platform based on its name
        getPlatformColor(platformName) {
            const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316']
            
            // Create a simple hash of the platform name to consistently assign colors
            let hash = 0
            for (let i = 0; i < platformName.length; i++) {
                const char = platformName.charCodeAt(i)
                hash = ((hash << 5) - hash) + char
                hash = hash & hash // Convert to 32-bit integer
            }
            
            // Use absolute value and modulo to get consistent color index
            const colorIndex = Math.abs(hash) % colors.length
            return colors[colorIndex]
        },

        getMarketShareOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const label = context.dataset.label
                                const value = context.parsed.y
                                if (label === 'Market Share %') {
                                    return `${label}: ${value.toFixed(1)}%`
                                }
                                return `${label}: ${value.toLocaleString()}`
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: { display: true, text: 'Customer Count' }
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: { display: true, text: 'Market Share %' },
                        grid: { drawOnChartArea: false }
                    }
                }
            }
        },

        getNetChangeOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const value = context.parsed.y
                                return `Δ ${value >= 0 ? '+' : ''}${value}`
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Net Change' }
                    }
                }
            }
        },

        getStackedBarOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const originalValue = context.dataset.originalData[context.dataIndex]
                                return `${context.dataset.label}: ${originalValue.toLocaleString()}`
                            }
                        }
                    }
                },
                scales: {
                    x: { 
                        stacked: true,
                        ticks: {
                            maxRotation: 45,
                            minRotation: 0
                        }
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        title: { display: true, text: 'Municipality Count (√ scale)' },
                        ticks: {
                            callback: function(value) {
                                const originalValue = Math.round(Math.pow(value / 10, 2))
                                return originalValue.toLocaleString()
                            }
                        }
                    }
                },
                elements: {
                    bar: {
                        borderWidth: 2,
                        minBarLength: 10
                    }
                }
            }
        },

        getWeeklyChecksOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                const value = context.parsed.y
                                return `Checks: ${value.toLocaleString()}`
                            }
                        }
                    },
                    legend: {
                        display: true,
                        position: 'top'
                    }
                },
                scales: {
                    x: {
                        display: true,
                        title: {
                            display: true,
                            text: 'Date'
                        },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 0,
                            maxTicksLimit: 15
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Number of Checks'
                        },
                        ticks: {
                            callback: function(value) {
                                return value.toLocaleString()
                            }
                        }
                    }
                },
                elements: {
                    point: {
                        hoverRadius: 8
                    }
                },
                interaction: {
                    intersect: false,
                    mode: 'index'
                }
            }
        }
    },

    beforeDestroy() {
        if (this.charts) {
            Object.values(this.charts).forEach(chart => {
                if (chart) chart.destroy()
            })
        }
    }
}
</script>

<style scoped>
.chart-card { min-height: 420px; }
.chart-wrap { position: relative; height: 300px; }
.chart-wrap canvas { max-height: 300px !important; }
.cursor-pointer { cursor: pointer; }
.kpi { font-weight: 700; font-size: 1.5rem; }
.kpi-sub { color: #6c757d; font-size: .9rem; }
.kpi-date { color: #8e8e93; font-size: .8rem; font-weight: 500; margin-top: 2px; }
.btn-check:checked + .btn-outline-primary {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
}
</style>